/**
 * LexiReview Admin — Main App Component
 *
 * Admin-only routes served on admin.lexireview.in.
 * All pages are lazy-loaded and protected behind admin role check.
 */

import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { lazy, Suspense, useState } from 'react'
import { Loader2, Shield } from 'lucide-react'
import AdminLayout from './components/layout/AdminLayout'
import { authService } from './services/auth.service'

// ---------------------------------------------------------------------------
// Suspense fallback
// ---------------------------------------------------------------------------
function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-label="Loading" />
        <p className="text-sm text-muted-foreground font-medium font-body">Loading...</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lazy-loaded admin pages
// ---------------------------------------------------------------------------
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const PlatformHealth = lazy(() => import('./pages/PlatformHealth'))
const FinancialOps = lazy(() => import('./pages/FinancialOps'))
const LegalBrainDashboard = lazy(() => import('./pages/LegalBrainDashboard'))
const LearningQueue = lazy(() => import('./pages/LearningQueue'))
const OrgManagement = lazy(() => import('./pages/OrgManagement'))
const RuleDatabase = lazy(() => import('./pages/RuleDatabase'))
const RuleCategory = lazy(() => import('./pages/RuleCategory'))
const RuleDetail = lazy(() => import('./pages/RuleDetail'))
const ComplianceScorecard = lazy(() => import('./pages/ComplianceScorecard'))
const Notifications = lazy(() => import('./pages/Notifications'))
const SupportTickets = lazy(() => import('./pages/SupportTickets'))

// ---------------------------------------------------------------------------
// Login page for admin (simple redirect to main app)
// ---------------------------------------------------------------------------
function AdminLogin() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      // POSTs to /auth/signin with the admin origin (passes CORS + CSRF, both keyed
      // on CORS_ORIGIN which now includes admin.lexireview.in). The server sets the
      // .lexireview.in session cookie; a fresh sign-in mints a token carrying the
      // current role, so admins get role=admin immediately (no stale-token issue).
      await authService.login({ identifier: email.trim(), password })
      navigate('/', { replace: true })
    } catch (err: any) {
      setError(err?.message || 'Sign in failed. Check your email and password.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-navy-950 px-4">
      <div className="bg-card rounded-2xl shadow-elevated border border-border p-8 max-w-md w-full">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-semibold font-heading text-foreground">LexiReview Admin</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Platform administrators only</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5 font-body">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@company.com"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-blue-600 font-body"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5 font-body">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-blue-600 font-body"
            />
          </div>
          {error && <p className="text-sm text-red-500 font-body" role="alert">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-2.5 rounded-xl font-medium transition-colors font-body text-sm"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Login redirect */}
          <Route path="/login" element={<AdminLogin />} />

          {/* Admin routes — protected + layout */}
          <Route element={<ProtectedRoute />}>
            <Route path="/*" element={
              <AdminLayout>
                <Routes>
                  {/* Overview */}
                  <Route path="/" element={<AdminDashboard />} />

                  {/* Platform */}
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/platform" element={<PlatformHealth />} />
                  <Route path="/finance" element={<FinancialOps />} />
                  <Route path="/organizations" element={<OrgManagement />} />

                  {/* Legal Intelligence */}
                  <Route path="/legal-brain" element={<LegalBrainDashboard />} />
                  <Route path="/learning" element={<LearningQueue />} />
                  <Route path="/rule-db" element={<RuleDatabase />} />
                  <Route path="/rule-db/:category" element={<RuleCategory />} />
                  <Route path="/rule-db/:category/:ruleId" element={<RuleDetail />} />
                  <Route path="/compliance" element={<ComplianceScorecard />} />

                  {/* Engagement */}
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/support" element={<SupportTickets />} />

                  {/* 404 fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AdminLayout>
            } />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

export default App
